import type { CommerceConfigContextValues } from '@/types/commerce';
import {
	type CommerceOffer,
	type CommerceKeyValueMapper,
	mergeLinkWrapperDefinitions,
	type CommerceLinkWrapVendor,
	identifyLinkWrappersForOffer,
	type CommerceLinkWrapperConfig,
	type BaseCommerceSingleEmbedConfig,
	type CommerceLinkWrapVendorDefinition,
	type CommerceLinkWrapVendorDefinitionSet,
} from '@themaven-net/commerce-shared';

/**
 * Build a link wrapper config object using the commerce config context and disable flags
 */
export function getLinkWrapperConfigMapper(
	commerceConfigContext: CommerceConfigContextValues,
	disableSkimlinks: boolean,
	disableTrackonomics: boolean,
): CommerceKeyValueMapper<CommerceLinkWrapperConfig> {
	return {
		Skimlinks: () =>
			disableSkimlinks ? undefined : commerceConfigContext.skimlinks,
		Trackonomics: () =>
			disableTrackonomics ? undefined : commerceConfigContext.trackonomics,
	};
}

/**
 * Get a link wrap vendor config from the context by vendor name
 */
export function getLinkWrapVendorFromContext(
	vendor: CommerceLinkWrapVendor,
	commerceConfigContext: CommerceConfigContextValues,
): CommerceLinkWrapperConfig | undefined {
	switch (vendor) {
		case 'Skimlinks':
			return commerceConfigContext.skimlinks;
		case 'Trackonomics':
			return commerceConfigContext.trackonomics;
		default:
			// eslint-disable-next-line no-console
			console.warn(
				'Attempting to get an invalid link wrap vendor config',
				vendor,
			);
	}

	return undefined;
}

/**
 * Merge an embed config link wrapper definition with its context counterpart
 */
export function mergeLinkWrapperConfigs(
	partialWrappers: CommerceLinkWrapperConfig[] | undefined,
	commerceConfigContext: CommerceConfigContextValues,
): CommerceLinkWrapperConfig[] {
	if (partialWrappers === undefined) {
		return [];
	}
	const confMap: CommerceLinkWrapVendorDefinitionSet = {
		Skimlinks: getLinkWrapVendorFromContext(
			'Skimlinks',
			commerceConfigContext,
		) || {
			disabled: false,
			vendor: 'Skimlinks',
		},
		Trackonomics: getLinkWrapVendorFromContext(
			'Trackonomics',
			commerceConfigContext,
		) || {
			disabled: false,
			vendor: 'Trackonomics',
		},
	};

	return mergeLinkWrapperDefinitions(partialWrappers, confMap);
}

/**
 * Build and merge all link wrapper configs for an offer from the embed and context.
 */
export function buildLinkWrapConfigsForOffer(
	offer: CommerceOffer | undefined,
	commerceConfig: BaseCommerceSingleEmbedConfig | undefined,
	commerceConfigContext: CommerceConfigContextValues,
): CommerceLinkWrapperConfig[] {
	if (offer === undefined) {
		return [];
	}

	const linkWrapperConfigs = identifyLinkWrappersForOffer(
		offer,
		commerceConfig?.options?.linkWrappersByIdentifier,
	);

	return mergeLinkWrapperConfigs(linkWrapperConfigs, commerceConfigContext);
}

export function isLinkWrapVendorEnabledInConfig(
	vendor: CommerceLinkWrapVendor,
	configArray: CommerceLinkWrapVendorDefinition[],
): boolean {
	const selected = configArray.find((config) => config.vendor === vendor);

	if (selected === undefined || selected.disabled === true) {
		return false;
	}

	return true;
}
